import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ReadFieldProps {
  label: string;
  labelWidth?: string;
  value: ReactNode;
  unit?: string;
  className?: string;
  /** When true, render an empty value as a dim em-dash. Defaults to true. */
  placeholderEmpty?: boolean;
  /** When true, allow the value to wrap onto multiple lines (mirrors a Textarea). */
  multiline?: boolean;
}

/**
 * Read-only label-left / value-right row used by Service view pages.
 * Mirrors the row shape used by the edit form (FormItem > flex items-center gap-4),
 * so view and edit pages line up visually.
 */
export const ReadField = ({
  label,
  labelWidth = "170px",
  value,
  unit,
  className,
  placeholderEmpty = true,
  multiline = false,
}: ReadFieldProps) => {
  const isEmpty =
    value === "" ||
    value === null ||
    value === undefined ||
    (typeof value === "number" && Number.isNaN(value));

  return (
    <div
      className={cn(
        "flex gap-4",
        multiline ? "items-start" : "items-center",
        className
      )}
    >
      <span
        className={cn(
          "z-label shrink-0 mb-0 text-muted-foreground",
          multiline && "pt-2"
        )}
        style={{ width: labelWidth }}
      >
        {label}
      </span>
      <div className="relative flex-1 min-w-0">
        <div
          className={cn(
            "z-body w-full rounded-md border border-input bg-muted/30 px-3 text-foreground",
            multiline
              ? "min-h-9 py-2 whitespace-pre-wrap break-words"
              : "flex h-9 items-center py-1",
            unit ? "pr-10" : ""
          )}
        >
          {multiline ? (
            isEmpty && placeholderEmpty ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              value
            )
          ) : (
            <span className="truncate">
              {isEmpty && placeholderEmpty ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                value
              )}
            </span>
          )}
        </div>
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 z-hint text-muted-foreground pointer-events-none">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
};
